import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import 'leaflet/dist/leaflet.css';

/*
 * Plain Leaflet (not react-leaflet) -- react-leaflet's current ESM build
 * uses syntax (nullish coalescing in a node_modules package) this
 * project's CRA/webpack4/Babel setup won't parse without ejecting or
 * adding a custom transform pipeline. Leaflet itself has no such issue,
 * and the imperative API is a well-established way to use it from React
 * via a ref, without that dependency.
 */

// Webpack/CRA doesn't resolve Leaflet's default marker image paths on its
// own -- without this, every marker silently renders as a broken image.
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
	iconRetinaUrl: markerIcon2x,
	iconUrl: markerIcon,
	shadowUrl: markerShadow
});

const centerIcon = new L.Icon({
	iconRetinaUrl: markerIcon2x,
	iconUrl: markerIcon,
	shadowUrl: markerShadow,
	iconSize: [30, 49],
	iconAnchor: [15, 49],
	className: 'destination-map__center-marker'
});

// Popup content is plain HTML handed to Leaflet, not mounted React --
// react-router's <Link> throws if rendered outside a <Router> (which is
// exactly what happens via renderToStaticMarkup), so this is a plain <a>;
// clicking it is a full page load rather than a client-side transition,
// which is fine since the Rails catch-all serves the SPA shell for any path.
function escapeHtml(str) {
	return String(str).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

/**
 * center: { lat, lng, name } -- the destination itself.
 * nearby: [{ slug, name, latitude, longitude }] -- other destinations with
 * coordinates, already fetched as part of the page (no extra API calls).
 */
function DestinationMap({ center, nearby = [] }) {
	const containerRef = useRef(null);
	const mapRef = useRef(null);

	useEffect(() => {
		if (!center.lat || !center.lng || !containerRef.current) return undefined;

		const nearbyWithCoords = nearby.filter((n) => n.latitude && n.longitude);
		const map = L.map(containerRef.current, { scrollWheelZoom: false })
			.setView([center.lat, center.lng], nearbyWithCoords.length > 0 ? 11 : 13);
		mapRef.current = map;

		L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
			attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
		}).addTo(map);

		L.marker([center.lat, center.lng], { icon: centerIcon }).addTo(map).bindPopup(escapeHtml(center.name));

		nearbyWithCoords.forEach((n) => {
			const popupHtml = `<a href="/destinations/${escapeHtml(n.slug)}">${escapeHtml(n.name)}</a>`;
			L.marker([n.latitude, n.longitude]).addTo(map).bindPopup(popupHtml);
		});

		// Leaflet reads its container's size on init -- if the container
		// wasn't done laying out yet (common right after mount inside a
		// grid), tiles render at the wrong size until a resize fires.
		setTimeout(() => map.invalidateSize(), 0);

		return () => {
			map.remove();
			mapRef.current = null;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [center.lat, center.lng]);

	if (!center.lat || !center.lng) return null;

	return <div ref={containerRef} className="destination-map" />;
}

export default DestinationMap;
